import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Checkbox, Ltr, Select, useToast } from '../components/ui';
import { IconUpload } from '../components/icons';

type Field = 'sku' | 'name' | 'price' | 'cost' | 'description' | 'category' | 'unit' | 'unitsPerPack';
type Row = {
  rowNumber: number;
  sku: string;
  name: string;
  price: number | null;
  cost?: number | null;
  description: string | null;
  category: string | null;
  unit: string | null;
  action: 'create' | 'update' | 'same' | 'error';
  errors: string[];
  warnings: string[];
  changes: string[];
};
type Preview = {
  headers: string[];
  mapping: Record<string, Field | null>;
  rows: Row[];
  needsMapping: boolean;
  vatInHeader: boolean;
  hasPrice: boolean;
  costsHidden?: boolean;
  fields: Field[];
  summary: { create: number; update: number; same: number; error: number; notInFile: number; inPriceFile: number } | null;
};
type Result = { created: number; updated: number; deactivated: number; pricesFromFile: number; costsUpdated?: number; withoutPrice: number; matching: { matched: number; pending: number } };

const FIELD_LABELS: Record<Field, string> = { sku: 'מק״ט', name: 'שם מוצר', price: 'מחיר', cost: 'עלות (לפני מע״מ)', description: 'תיאור', category: 'קטגוריה', unit: 'יחידה', unitsPerPack: 'יחידות בחבילה' };
const ACTION = { create: { label: 'חדש', tone: 'ok' }, update: { label: 'עדכון', tone: 'info' }, same: { label: 'ללא שינוי', tone: '' }, error: { label: 'שגיאה', tone: 'bad' } } as const;

export function ProductImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [includesVat, setIncludesVat] = useState(false);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [showSame, setShowSame] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const runPreview = async (f: File, opts: { mapping?: Record<string, Field | null>; vat?: boolean } = {}) => {
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('includesVat', String(opts.vat ?? includesVat));
      if (opts.mapping) form.append('mapping', JSON.stringify(opts.mapping));
      form.append('file', f);
      setPreview(await api<Preview>('/api/products/import/preview', { form }));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const valid = preview?.rows.filter((r) => r.action !== 'error') ?? [];
  const changed = valid.filter((r) => r.action !== 'same').length;

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      // All valid rows are sent (also unchanged ones), so "mark missing as inactive" knows the whole file.
      const rows = valid.map(({ action: _a, errors: _e, warnings: _w, changes: _c, ...r }) => r);
      const r = await api<Result>('/api/products/import/commit', { body: { rows, deactivateMissing } });
      setResult(r);
      for (const k of ['products', 'product-matching', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const shown = preview?.rows.filter((r) => showSame || r.action !== 'same') ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/products">קטלוג מוצרים</Link></div>
          <h1>ייבוא קובץ קטלוג</h1>
          <div className="sub">
            CSV או Excel עם מק״ט ושם מוצר (קטגוריה ויחידה — לא חובה). המוצרים מזוהים לפי מק״ט — קובץ חוזר מעדכן שמות, בלי כפילויות.
            המחירים מגיעים מ<Link to="/products/prices">קובץ המחירים</Link> ומתחברים לפי שם המוצר.
          </div>
        </div>
      </div>

      {result ? (
        <div className="panel panel-body stack">
          <Callout tone="ok" title="הקטלוג עודכן">
            {fmtNumber(result.created)} מוצרים חדשים · {fmtNumber(result.updated)} עודכנו
            {result.deactivated ? ` · ${fmtNumber(result.deactivated)} סומנו כלא פעילים` : ''}
            {result.pricesFromFile ? ` · ${fmtNumber(result.pricesFromFile)} קיבלו מחיר מקובץ המחירים` : ''}
            {result.costsUpdated ? ` · ${fmtNumber(result.costsUpdated)} עלויות עודכנו` : ''}
          </Callout>
          {result.withoutPrice ? (
            <Callout tone="warn" action={<Link to="/products/prices" className="btn sm">ייבוא מחירים</Link>}>
              {fmtNumber(result.withoutPrice)} מוצרים עדיין בלי מחיר — יקבלו אותו כשיועלה קובץ מחירים עם אותם שמות.
            </Callout>
          ) : null}
          <Callout tone={result.matching.pending ? 'info' : 'ok'} title="הצלבה עם הצעות מחיר וחשבוניות">
            {fmtNumber(result.matching.matched)} תיאורי שורות מהמסמכים שויכו אוטומטית למוצרים.
            {result.matching.pending ? ` ${fmtNumber(result.matching.pending)} תיאורים ממתינים לשיוך ידני — מסודרים לפי שכיחות, כך שכמה דקות מכסות את רוב השורות.` : ''}
          </Callout>
          <div className="row">
            {result.matching.pending ? <Link to="/products/matching" className="btn primary">לשיוך שורות</Link> : null}
            <Link to="/products" className="btn">לקטלוג</Link>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="panel panel-body stack">
            <div className="dropzone" onClick={() => input.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}>
              {busy && !preview ? (
                <div className="row" style={{ justifyContent: 'center' }}><span className="spinner" /> קורא את הקובץ…</div>
              ) : (
                <>
                  <IconUpload size={22} />
                  <div className="strong" style={{ marginTop: 8 }}>{file ? file.name : 'בחירת קובץ Excel או CSV'}</div>
                  <div className="small muted">{file ? 'לחצו להחלפת קובץ' : 'העמודות יזוהו אוטומטית — ניתן לתקן את ההתאמה'}</div>
                </>
              )}
            </div>
            <input
              ref={input}
              type="file"
              hidden
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setFile(f);
                  void runPreview(f);
                }
                e.target.value = '';
              }}
            />
            {preview && !preview.hasPrice ? null : <Checkbox
              checked={includesVat}
              onChange={(v) => {
                setIncludesVat(v);
                if (file && preview) void runPreview(file, { mapping: preview.mapping, vat: v });
              }}
            >
              המחירים בקובץ כוללים מע״מ (יומרו למחיר לפני מע״מ, כמו בשורות המסמכים)
            </Checkbox>}
            {preview?.costsHidden ? <Callout>בקובץ יש עמודת עלות — היא לא תיובא (נדרשת הרשאת "רווחיות ועלויות").</Callout> : null}
            {preview?.vatInHeader && !includesVat ? <Callout tone="warn">כותרת עמודת המחיר מציינת "כולל מע״מ" — אם כך, סמנו את התיבה למעלה.</Callout> : null}
          </div>

          {preview ? (
            <>
              <section className="panel">
                <div className="panel-head"><h2>התאמת עמודות</h2></div>
                <div className="panel-body">
                  <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                    {preview.headers.map((h) => (
                      <label key={h} className="field">
                        <span className="field-label ellipsis" title={h}>{h}</span>
                        <Select
                          value={preview.mapping[h] ?? ''}
                          onChange={(e) => {
                            const next = { ...preview.mapping };
                            const val = (e.target.value || null) as Field | null;
                            if (val) for (const k of Object.keys(next)) if (next[k] === val) next[k] = null;
                            next[h] = val;
                            if (file) void runPreview(file, { mapping: next });
                          }}
                        >
                          <option value="">לא לייבא</option>
                          {preview.fields.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                        </Select>
                      </label>
                    ))}
                  </div>
                  {preview.needsMapping ? <Callout tone="warn">יש לבחור את העמודות של מק״ט ושם מוצר.</Callout> : null}
                </div>
              </section>

              {preview.summary ? (
                <section className="panel">
                  <div className="panel-head row wrap">
                    <h2>{fmtNumber(preview.rows.length)} שורות</h2>
                    <span className="badge ok">{fmtNumber(preview.summary.create)} חדשים</span>
                    <span className="badge info">{fmtNumber(preview.summary.update)} עדכונים</span>
                    <span className="badge">{fmtNumber(preview.summary.same)} ללא שינוי</span>
                    {preview.summary.error ? <span className="badge bad">{fmtNumber(preview.summary.error)} שגיאות (לא ייובאו)</span> : null}
                    <span className="spacer" />
                    <Checkbox checked={showSame} onChange={setShowSame}>הצגת שורות ללא שינוי</Checkbox>
                  </div>
                  {preview.summary.inPriceFile ? (
                    <div className="panel-body small muted" style={{ paddingBottom: 0 }}>
                      ל-{fmtNumber(preview.summary.inPriceFile)} מהמוצרים כבר יש מחיר בקובץ המחירים — הוא יחובר אוטומטית.
                    </div>
                  ) : null}
                  {preview.summary.notInFile ? (
                    <div className="panel-body" style={{ paddingBottom: 0 }}>
                      <Checkbox checked={deactivateMissing} onChange={setDeactivateMissing}>
                        לסמן כ"לא פעיל" {fmtNumber(preview.summary.notInFile)} מוצרים שקיימים בקטלוג ואינם בקובץ (לא נמחקים — ההיסטוריה נשמרת)
                      </Checkbox>
                    </div>
                  ) : null}
                  <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>שורה</th>
                          <th>מק״ט</th>
                          <th>שם מוצר</th>
                          {preview.hasPrice ? <th className="num">מחיר לפני מע״מ</th> : null}
                          <th className="hide-sm">קטגוריה</th>
                          <th>פעולה</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((r) => (
                          <tr key={r.rowNumber} style={r.action === 'error' ? { background: 'var(--surface-2)' } : undefined}>
                            <td className="muted tabular">{r.rowNumber}</td>
                            <td className="tabular">{r.sku ? <Ltr>{r.sku}</Ltr> : <span className="faint">—</span>}</td>
                            <td className="primary">{r.name || <span className="faint">—</span>}</td>
                            {preview.hasPrice ? <td className="num tabular">{r.price == null ? <span className="faint">—</span> : fmtMoney(r.price)}</td> : null}
                            <td className="hide-sm small">{r.category ?? <span className="faint">—</span>}</td>
                            <td className="small" style={{ maxWidth: 320 }}>
                              <span className={`badge ${ACTION[r.action].tone}`}>{ACTION[r.action].label}</span>
                              {r.errors.map((e) => <div key={e} style={{ color: 'var(--bad)' }}>{e}</div>)}
                              {r.changes.map((c) => <div key={c} className="muted">{c}</div>)}
                              {r.warnings.map((w) => <div key={w} style={{ color: 'var(--warn)' }}>{w}</div>)}
                            </td>
                          </tr>
                        ))}
                        {shown.length === 0 ? <tr><td colSpan={preview.hasPrice ? 6 : 5} className="muted">אין שינויים — הקטלוג כבר תואם לקובץ.</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="panel-foot row wrap">
                    <Button variant="primary" onClick={commit} loading={busy} disabled={valid.length === 0 || (changed === 0 && !deactivateMissing)}>
                      ייבוא — {fmtNumber(preview.summary.create)} חדשים, {fmtNumber(preview.summary.update)} עדכונים
                    </Button>
                    <span className="small muted">אחרי הייבוא המוצרים יוצלבו אוטומטית עם השורות בהצעות המחיר ובחשבוניות.</span>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </>
  );
}
